import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Server, Monitor, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const useErrorCapture = () => {
  const [errors, setErrors] = useState({ frontend: [], backend: [] });

  useEffect(() => {
    console.log('ErrorLogs component mounted');

    const loadErrors = () => {
      const storedErrors = JSON.parse(localStorage.getItem('appErrors') || '{"frontend":[],"backend":[]}');
      console.log('Loaded errors from localStorage:', storedErrors);
      setErrors(storedErrors);
    };

    loadErrors();

    const intervalId = setInterval(loadErrors, 5000);

    const handleFrontendError = (event) => {
      console.log('Captured frontend error:', event);
      logError('frontend', event.error.message, event.error.stack);
    };

    window.addEventListener('error', handleFrontendError);
    window.addEventListener('unhandledrejection', (event) => {
      handleFrontendError({ error: event.reason });
    });

    const interceptor = axios.interceptors.response.use(
      response => response,
      error => {
        console.log('Captured axios error:', error);
        logError('backend', error.response?.data?.message || error.message, error.stack);
        return Promise.reject(error);
      }
    );

    const logError = (type, message, stack) => {
      console.log('Logging error:', type, message, stack);
      const newError = {
        id: Date.now(),
        message,
        stack,
        timestamp: new Date().toISOString(),
      };
      const updatedErrors = JSON.parse(localStorage.getItem('appErrors') || '{"frontend":[],"backend":[]}');
      updatedErrors[type].push(newError);
      localStorage.setItem('appErrors', JSON.stringify(updatedErrors));
      setErrors(updatedErrors);
    };

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('error', handleFrontendError);
      window.removeEventListener('unhandledrejection', handleFrontendError);
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  return errors;
};

const ErrorLogs = () => {
  const errors = useErrorCapture();
  const [activeTab, setActiveTab] = useState('frontend');
  const [expandedErrors, setExpandedErrors] = useState({});

  const toggleErrorExpansion = (id) => {
    setExpandedErrors(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const ErrorCard = ({ error }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="bg-white dark:bg-gray-800 rounded-lg shadow-md dark:shadow-gray-900/20 overflow-hidden mb-4"
    >
      <div 
        className="p-4 cursor-pointer flex justify-between items-center"
        onClick={() => toggleErrorExpansion(error.id)}
      >
        <div className="flex items-center space-x-3">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <span className="font-medium text-gray-700">{error.message}</span>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-sm text-gray-500">{new Date(error.timestamp).toLocaleString()}</span>
          {expandedErrors[error.id] ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
        </div>
      </div>
      <AnimatePresence>
        {expandedErrors[error.id] && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-gray-50 dark:bg-gray-900 p-4 border-t border-gray-200 dark:border-gray-700"
          >
            <pre className="text-sm text-gray-600 whitespace-pre-wrap overflow-x-auto">{error.stack}</pre>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );

  return (
    <div className="space-y-6">
      <h2 className="text-3xl font-bold text-gray-900 mb-6">Error Logs</h2>
      <div className="flex space-x-4 mb-6">
        <button
          onClick={() => setActiveTab('frontend')}
          className={`px-4 py-2 rounded-md transition-colors duration-200 ${
            activeTab === 'frontend' ? 'bg-primary-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          <div className="flex items-center space-x-2">
            <Monitor className="w-5 h-5" />
            <span>Frontend Errors ({errors.frontend.length})</span>
          </div>
        </button>
        <button
          onClick={() => setActiveTab('backend')}
          className={`px-4 py-2 rounded-md transition-colors duration-200 ${
            activeTab === 'backend' ? 'bg-primary-600 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600'
          }`}
        >
          <div className="flex items-center space-x-2">
            <Server className="w-5 h-5" />
            <span>Backend Errors ({errors.backend.length})</span>
          </div>
        </button>
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 20 }}
          transition={{ duration: 0.3 }}
        >
          {errors[activeTab].length > 0 ? (
            errors[activeTab].map(error => <ErrorCard key={error.id} error={error} />)
          ) : (
            <p className="text-gray-500 text-center">No {activeTab} errors captured.</p>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default ErrorLogs;